import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaUserPlus, FaCheck } from 'react-icons/fa';
import debounce from 'lodash/debounce';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useSelector, useDispatch } from 'react-redux';
import { searchUsers } from '../../../state/connectionSlice';
import { IoSearch, IoClose, IoCheckmarkCircle, IoPerson, IoHome, IoPeople } from 'react-icons/io5';
import { FaUserFriends, FaUsers, FaHeart, FaUserTie } from 'react-icons/fa';
import ProfileCard from '../../../components/ProfileCard';
import ReactDOM from 'react-dom';

const ConnectionSearch = ({ onSearch, onSelectUser, selectedUsers = [], pendingRequests = {}, connections = [] }) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [selectedProfileUser, setSelectedProfileUser] = useState(null);
  const searchResults = useSelector(state => state.connections.searchResults);
  const isSearching = useSelector(state => state.connections.loading.searchUsers);
  const debouncedSearchRef = useRef(null);

  // Create a stable debounced search function
  useEffect(() => {
    debouncedSearchRef.current = debounce(async (query) => {
      if (!query.trim()) {
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Dispatch the searchUsers action with the current user ID
        await dispatch(searchUsers({
          query,
          userId: user.id
        }));
        
        // Notify parent component about the search
        if (onSearch) {
          onSearch(query);
        }
      } catch (error) {
        // Handle error silently
      } finally {
        setIsLoading(false);
      }
    }, 300);
    
    // Cleanup
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [dispatch, onSearch, user.id]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim()) {
      debouncedSearchRef.current(query);
    }
  };

  // Function to get profile image URL with fallback
  const getProfileImageUrl = (user) => {
    if (!user) return null;
    
    // Try all possible image properties
    const imageUrl = user.profilePhotoUrl || 
                    user.profile_photo_url || 
                    user.avatar || 
                    user.imageUrl || 
                    user.photo;
                    
    // If no image is found, return null to use the fallback
    return imageUrl || null;
  };

  // Helper function to render user's name consistently
  const renderUserName = (user) => {
    if (!user) return 'Unknown User';
    return user.name || user.username || user.displayName || 'Anonymous User';
  };

  // Get user initials for avatar placeholder
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // Handle user selection
  const handleUserSelect = (userId) => {
    try {
      if (onSelectUser) {
        onSelectUser(userId);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  // New function to handle viewing user profile
  const handleViewProfile = (user) => {
    setSelectedProfileUser({
      id: user.id || user._id,
      name: renderUserName(user),
      profilePhotoUrl: getProfileImageUrl(user),
      jobTitle: user.jobTitle || user.job_title || user.occupation || 'User on Jingleez',
      location: user.location || user.city
    });
    setShowProfileCard(true);
  };

  // Check if a user is selected
  const isUserSelected = (userId) => {
    return selectedUsers.includes(userId);
  };

  // Filter out the connected users from the search results
  const filteredSearchResults = Array.isArray(searchResults) 
    ? searchResults.filter(result => {
        // Check if user is already connected
        const isConnected = connections?.some(conn => 
          conn.id === result.id || 
          conn._id === result.id ||
          conn.connectedUserId === result.id
        );
        // Only include users who are not already connected
        return !isConnected;
      })
    : [];

  // Debug logs to help identify the issue
  console.log('Search results:', searchResults);
  console.log('Filtered search results:', filteredSearchResults);

  return (
    <div className="w-full mx-auto">
      {/* Search Bar */}
      <div className="relative mb-6">
        <div className="relative flex items-center rounded-xl overflow-hidden shadow-lg border border-soft-pink/20 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60">
          <div className="pl-4">
            <IoSearch className="h-5 w-5 text-soft-pink" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by name or email..."
            className="w-full px-3 py-3 bg-transparent text-pri-white placeholder-pri-gray/60 focus:outline-none"
          />
          {searchQuery && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setSearchQuery('')}
              className="pr-3"
            >
              <IoClose className="h-5 w-5 text-pri-gray hover:text-pri-white transition-colors" />
            </motion.button>
          )}
        </div>
        
        {/* Search Spinner */}
        {isLoading && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
              className="w-5 h-5 border-2 border-soft-pink/30 border-t-soft-pink rounded-full"
            />
          </div>
        )}
      </div>

      <AnimatePresence>
        {filteredSearchResults.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-2"
          >
            <div className="text-sm font-medium text-pri-gray mb-2">
              Search Results ({filteredSearchResults.length})
            </div>
            
            {filteredSearchResults.map((result) => {
              // Check if user already has a pending connection request
              const hasPendingIncoming = pendingRequests?.incoming?.some(
                req => req.requesterId === result.id || req.requesterId === result._id
              );
              
              const hasPendingOutgoing = pendingRequests?.outgoing?.some(
                req => req.recipientId === result.id || req.recipientId === result._id
              );
              
              // Check if user is already selected
              const isSelected = isUserSelected(result.id || result._id);
              
              return (
                <motion.div
                  key={result.id || result._id || `search-result-${Date.now()}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className={`flex items-center justify-between p-3 rounded-xl bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 border ${
                    isSelected 
                      ? 'border-soft-pink/40 shadow-lg'
                      : 'border-soft-pink/10 hover:border-soft-pink/30'
                  } transition-all`}
                >
                  <div className="flex items-center space-x-3 cursor-pointer" onClick={() => handleViewProfile(result)}>
                    {/* User Avatar */}
                    <div className="relative w-12 h-12 flex-shrink-0">
                      {getProfileImageUrl(result) ? (
                        <img
                          src={getProfileImageUrl(result)}
                          alt={renderUserName(result)}
                          className="w-full h-full rounded-full object-cover border border-soft-pink/20"
                        />
                      ) : (
                        <div className="w-full h-full rounded-full flex items-center justify-center bg-gradient-to-br from-soft-pink/20 to-mint-green/20 text-white">
                          {getInitials(renderUserName(result))}
                        </div>
                      )}
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-pri-white truncate">
                        {renderUserName(result)}
                      </h3>
                      <p className="text-sm text-pri-gray truncate">
                        {result.bio || result.email || "User on Jingleez"}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    {hasPendingIncoming ? (
                      <span className="px-3 py-1 text-xs font-medium text-pri-yellow bg-pri-yellow/10 rounded-full">
                        Request Received
                      </span>
                    ) : hasPendingOutgoing ? (
                      <span className="px-3 py-1 text-xs font-medium text-mint-green bg-mint-green/10 rounded-full">
                        Request Sent
                      </span>
                    ) : (
                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleUserSelect(result.id || result._id)}
                        className={`flex items-center justify-center p-2 rounded-full ${
                          isSelected 
                            ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark'
                            : 'bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 text-soft-pink border border-soft-pink/30'
                        }`}
                      >
                        {isSelected ? (
                          <FaCheck className="w-4 h-4" />
                        ) : (
                          <FaUserPlus className="w-4 h-4" />
                        )}
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : searchQuery && !isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center p-6 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 rounded-xl border border-soft-pink/10"
          >
            <div className="flex justify-center mb-3">
              <IoPerson className="w-10 h-10 text-soft-pink/40" />
            </div>
            <p className="text-pri-gray mb-1">No users found matching "{searchQuery}"</p>
            <p className="text-sm text-pri-gray/70">Try a different search term</p>
          </motion.div>
        ) : searchQuery.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center p-6 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 rounded-xl border border-soft-pink/10"
          >
            <div className="flex justify-center mb-3">
              <IoPeople className="w-10 h-10 text-soft-pink/40" />
            </div>
            <p className="text-pri-gray mb-1">Search for users to connect with</p>
            <p className="text-sm text-pri-gray/70">Try searching by name or email</p>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Profile Card Modal */}
      {showProfileCard && selectedProfileUser && (
        <ProfileCardPortal user={selectedProfileUser} onClose={() => setShowProfileCard(false)} isOpen={showProfileCard} />
      )}
    </div>
  );
};

// ProfileCardPortal component for ConnectionSearch
const ProfileCardPortal = ({ user, onClose, isOpen }) => {
  if (!isOpen || !user) return null;
  
  // Create a portal to render the profile card outside the search component
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-lg"
         style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
    >
      <div 
        className="absolute inset-0"
        onClick={() => {
          console.log('Backdrop clicked, closing profile card');
          onClose();
        }}
      ></div>
      
      <div 
        className="relative max-w-[90vw] z-[10000] bg-gradient-to-br from-[#1a1f36]/80 to-[#121524]/80 p-1 rounded-xl backdrop-blur-md shadow-2xl border border-soft-pink/30"
        onClick={(e) => e.stopPropagation()}
      >
        <ProfileCard 
          user={user}
          onClose={onClose}
          style={{ 
            maxWidth: '280px',
            width: '100%'
          }}
        />
      </div>
    </div>,
    document.body
  );
};

export default ConnectionSearch; 